import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FeedbackSurveyService {
  constructor(private http: HttpClient) {}

  /**
   * @description To get the list of all Feedback surveys.
   * @param accountId account id.
   * @param userId user id.
   * @returns observable.
   */
  public getFeedbackSurveysList(accountId: number, userId: number, page: number = 1, sortOrder: string): Observable<any> {
    // const payload = {'accounts': [85, 86, 87, 88, 171, 172, 173, 175, 495]}; // Add accounts array here.
    let url: string;
    const payloadObj = {
      iss: 'https://platform.renderseo.com', // Might have to change this url....
      sub: userId, // user id
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);
    if(environment.name === 'local' || environment.name === 'development') {
      url = `https://dev-io.renderseo.com/survey/results?timeunit=alltime&periods=current&page=${page}&orderby=date&order=${sortOrder}`
    } else {
      url = `https://io.renderseo.com/survey/results?timeunit=alltime&periods=current&page=${page}&orderby=date&order=${sortOrder}`
    }

    console.log('Survey results URL:', url);

    return this.http.get(url, { headers: headers }).pipe(
      tap((res) => {
        console.log('GET survey/results API response:', res);
        console.log('records:', res?.records);
        console.log('total:', res?.total);
        console.log('summary:', res?.summary);
      })
    );
  }

  /**
   * @description Update a survey response (Responded to, Internal user Comment, Responded by).
   * @param accountId account id.
   * @param userId user id (for JWT).
   * @param body request body: { account_ident, type, updates }.
   * @returns observable.
   */
  public updateSurveyResponse(
    accountId: number,
    userId: number,
    body: {
      account_ident: string;
      type: string;
      updates: Record<string, boolean | string | { user_id: number; name: string; email_id: string }>;
    }
  ): Observable<any> {
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const headers = new HttpHeaders()
      .set('Authorization', `${jwtToken}`)
      .set('Content-Type', 'application/json');
    const url =
      environment.name === 'local' || environment.name === 'development'
        ? 'https://dev-io.renderseo.com/survey/update'
        : 'https://io.renderseo.com/survey/update';
    return this.http.post(url, body, { headers });
  }

  /**
   * @description: Format Feedback survey data.
   * @returns: List of Feedback survey's
   * @arguments: List of Feedback survey's
   */
  /**
   * Format responded_by when API returns object { name, email_id }.
   */
  private formatRespondedBy(respondedBy: any): string {
    if (respondedBy == null) {
      return '';
    }
    if (typeof respondedBy === 'string') {
      return respondedBy;
    }
    if (typeof respondedBy === 'object' && respondedBy.name != null) {
      return respondedBy.email_id
        ? `${respondedBy.name} (${respondedBy.email_id})`
        : respondedBy.name;
    }
    return '';
  }


  /**
   * @description Normalizes survey records for table rendering and edit/save operations.
   * It preserves the original record shape while projecting UI-safe defaults and
   * harmonizing known API key variants (snake_case and camelCase) for critical fields.
   * @param surveyList Raw survey records from the survey results API.
   * @returns A normalized survey array; returns an empty array when input is not an array.
   */
  public formatFeedbackSurveyData(surveyList): any[] {
    if (!Array.isArray(surveyList)) {
      console.warn('formatFeedbackSurveyData: surveyList is not an array', surveyList);
      return [];
    }
    return surveyList.map((survey) => {
      const formatted = {
        ...survey,
        account_ident: survey?.account_ident ?? survey?.accountIdent ?? '',
        updatedDate: survey?.date?.slice(0, 16) || 'N/A',
        address: survey?.address ?? survey?.Address ?? 'N/A',
        check_number: survey?.check_number ?? 'N/A',
        comment: survey?.comment ?? survey?.comments ?? 'N/A',
        comments: survey?.comment ?? survey?.comments ?? 'N/A',
        contact: survey?.contact ?? 'N/A',
        date: survey?.date?.slice(0, 16) || 'N/A',
        rating: survey?.rating ?? 'N/A',
        storecode: survey?.store_code ?? survey?.storecode ?? 'N/A',
        type: survey?.feedback_type ?? survey?.type ?? 'N/A',
        record_type: survey?.type ?? null,
        responded_to: survey?.responded_to ?? false,
        internal_user_comment: survey?.internal_user_comment ?? '',
        responded_by: this.formatRespondedBy(survey?.responded_by) || 'N/A',
      };
      return formatted;
    });
  }
}
